import type { NotVerifiedAssetWithId } from '../../../../market/asset/AssetService.tsx';
import { Suspense, useCallback, useMemo, type ReactElement } from 'react';
import PortfolioDefinitionOptimizerContainer from './PortfolioDefinitionOptimizerContainer.tsx';
import type { StepConfig } from 'components/technical/wizard/StepConfig.ts';
import AssetScreenerTable from 'components/market/asset/assetScreener/AssetScreenerTable.tsx';
import { Stack } from '@mui/joy';
import GButton from 'components/technical/inputs/GButton/GButton.tsx';
import type {
  PortfolioDefinitionWithAssetScreenerInputFields,
  PortfolioDefinitionWithAssetScreenerOutputFields,
} from './PortfolioDefinitionWithAssetScreener.validation.ts';
import type { FilterModel } from 'ag-grid-community';
import { useFormContext } from 'react-hook-form';
import {
  convertFiltersToGridState,
  convertGridStateToFilters,
} from 'components/market/asset/assetScreener/AssetScreenerService.ts';
import { schema } from './PortfolioDefinitionWithAssetScreener.validation.ts';
import Loader from 'components/technical/Loader/Loader.tsx';
import { useLocation } from 'react-router-dom';
import { mapStateToDefaultValuesForScreener } from './PorttfolioDefinitionOptimizerService.ts';
import {
  type IRebalancedPortfolioFilterInput,
  usePortfolioDefinitionOptimizerInputSuspenseQuery,
} from 'generated/graphql.tsx';
import { UserSettings } from 'components/management/UserSettings.types.ts';

const PortfolioDefinitionAssetScreenerTable = ({
  assets,
  goToNextStep,
  defaultFilters,
}: {
  assets: NotVerifiedAssetWithId[];
  goToNextStep: () => void;
  defaultFilters: IRebalancedPortfolioFilterInput[];
}): ReactElement => {
  const { setValue } = useFormContext<PortfolioDefinitionWithAssetScreenerInputFields>();

  const handleFiltersChanged = useCallback(
    (model: FilterModel) => {
      const convertedFilters = convertGridStateToFilters(model);
      setValue('filters', convertedFilters);
    },
    [setValue]
  );

  return (
    <Suspense fallback={<Loader />}>
      <Stack height={'600px'}>
        <AssetScreenerTable
          presetKey={UserSettings.AssetScreenerPresets}
          assets={assets}
          scores={[]}
          onFiltersChanged={handleFiltersChanged}
          defaultFilters={convertFiltersToGridState(defaultFilters)}
        />
        <GButton
          onClick={goToNextStep}
          sx={{
            marginLeft: 'auto',
          }}
        >
          Next
        </GButton>
      </Stack>
    </Suspense>
  );
};

const assetScreenerUniverseStep = ({
  assets,
  goToNextStep,
  defaultFilters,
}: {
  assets: NotVerifiedAssetWithId[];
  goToNextStep: () => void;
  defaultFilters: IRebalancedPortfolioFilterInput[];
}): StepConfig<PortfolioDefinitionWithAssetScreenerInputFields> => ({
  label: 'Asset universe',
  element: (
    <PortfolioDefinitionAssetScreenerTable
      assets={assets}
      goToNextStep={goToNextStep}
      defaultFilters={defaultFilters}
    />
  ),
  fields: ['filters'],
});

const PortfolioDefinitionWithAssetScreenerWizard = () => {
  const location = useLocation();
  const { data } = usePortfolioDefinitionOptimizerInputSuspenseQuery();
  const defaultValues = useMemo(
    () => mapStateToDefaultValuesForScreener(location.state, data.assets),
    [location.state, data.assets]
  );
  const universeStep = useCallback(
    (props: {
      assets: NotVerifiedAssetWithId[];
      goToNextStep: () => void;
    }) =>
      assetScreenerUniverseStep({
        assets: props.assets,
        goToNextStep: props.goToNextStep,
        defaultFilters: defaultValues?.filters ?? [],
      }),
    [defaultValues?.filters]
  );

  return (
    <PortfolioDefinitionOptimizerContainer<
      PortfolioDefinitionWithAssetScreenerInputFields,
      PortfolioDefinitionWithAssetScreenerOutputFields
    >
      id={location.state?.id}
      universeStep={universeStep}
      schema={schema}
      createOptimizerRuleFilter={(portfolio) => ({
        assetIds: [],
        filters: portfolio.filters,
      })}
      defaultValues={defaultValues}
      assets={data.assets}
      multifactor={data.multifactor}
      rebalancingRules={location.state?.rebalancingRules ?? []}
    />
  );
};

export default PortfolioDefinitionWithAssetScreenerWizard;
